import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { User } from "@/api/types";
import * as authApi from "@/api/auth";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const initialChecked = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const scope = computed(() => user.value?.view_permission ?? "individual");
  // Asystentka: widzi klientów obsługiwanych doradców, bez modułów o pieniądzach (backend:
  // lib/permissions.ts). Nigdy admin — nawet gdyby konto miało omyłkowo view=global.
  const assistantFor = computed(() => user.value?.assistant_for ?? null);
  const isAssistant = computed(() => assistantFor.value !== null);
  const isAdmin = computed(() => !isAssistant.value && (user.value?.role_id === 1 || scope.value === "global"));
  // Domyślny właściciel nowego klienta/szansy/polisy: doradca dla asystentki, inaczej ja.
  const defaultOwnerId = computed<number | null>(() =>
    isAssistant.value ? (assistantFor.value?.[0]?.id ?? null) : (user.value?.id ?? null),
  );

  async function initFromSession(): Promise<void> {
    if (initialChecked.value) return;
    isLoading.value = true;
    try {
      user.value = await authApi.me();
    } finally {
      isLoading.value = false;
      initialChecked.value = true;
    }
  }

  async function loginOAuth(provider: "google" | "microsoft"): Promise<void> {
    isLoading.value = true;
    try {
      user.value = await authApi.loginOAuth(provider);
    } finally {
      isLoading.value = false;
    }
  }

  async function loginAsMock(userId: number): Promise<void> {
    isLoading.value = true;
    try {
      user.value = await authApi.loginAsMock(userId);
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<void> {
    await authApi.logout();
    user.value = null;
  }

  // Reaguj na 401 z client.ts
  window.addEventListener("auth:logout", () => {
    user.value = null;
  });

  return {
    user,
    isLoading,
    initialChecked,
    isAuthenticated,
    scope,
    isAdmin,
    assistantFor,
    isAssistant,
    defaultOwnerId,
    initFromSession,
    loginOAuth,
    loginAsMock,
    logout,
  };
});
